<template>
  <div class="gm-admin-tab">
    <server-table
      class="text-center groovemail-blocklist-email-table"
      ref="serverTable"
      :url="blockListEmailListing"
      tableRef="blacklistTable"
      :slotData="blockEmailTableSlotData"
      :tableColumns="blacklistMailTableColumns"
      :tableOptions="blacklistMailTableOptions"
      tableTitle="frontend_groovemail_admin_block_emails"
      addNewBtnText="frontend_groovemail_admin_block_emails"
      uploadFileBtnText="frontend_groovemail_admin_block_emails_via_import"
      :buttonViaImport="blockEmailButtonViaImport"
      downloadSampleFileBtnText="frontend_groovemail_admin_block_sample_file"
      @downloadSampleFile="downloadSampleForBlacklistingEmails"
      @onFileChange="onFileChange"
      @addNewData="openEmailBlacklistModal"
      @loaded="hideLoader()"
      @loading="showLoader()"
    >
    </server-table>

    <gs-named-modal
      id="blacklistEmailModal"
      name="blacklist-email-modal"
      :title="this.$t('frontend_groovemail_admin_block_emails')"
    >
      <div class="gm-admin-modal__body">
        <gs-field :label="$t('frontend_groovemail_admin_block_emails')">
          <text-area
            variant="base"
            v-model="model.emails"
            :placeholder="$t('frontend_groovemail_admin_block_emails_enter_emails')"
            rows="5"
          />
        </gs-field>
        <p class="gm-admin-tab__note">
          <span class="fa fa-info-circle"></span>
          {{ $t('frontend_groovemail_admin_block_emails_note') }}
        </p>
        <div class="gm-admin-modal__footer">
          <gs-button variant="outline" @click="closeEmailBlacklistModal()">{{ $t('frontend_cancel') }}</gs-button>
          <gs-button variant="primary" @click="addEmailsToBlacklist()">{{ $t('frontend_save') }}</gs-button>
        </div>
      </div>
    </gs-named-modal>

    <!-- Confirmation delete modal -->
    <confirm-modal
      :ref="`confirmation-delete-blacklisted`"
      :name="`confirmation-delete-blacklisted`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_blocked_emails_are_you_sure_delete_emails')"
      @confirm="removeBlacklistedMail"
      @close="closeDeleteBlacklistedModal"
    />

    <!-- Confirmation import csv -->
    <confirm-modal
      :ref="`confirmation-import-csv`"
      :name="`confirmation-import-csv`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_admin_block_emails_are_you_sure')"
      @confirm="confirmFileChnage"
      @close="closeFileChnageConfirm"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import { getData, postData, deleteData } from '@/utils/apiHelper.js';
import { adminUrls } from '@/utils/urls.js';
import { showRequestErrorAlert } from '@/components/showDialog.js';
import monthsMixin from '@/mixin/monthsMixin.js';
import demoModeValuesMixin from '@/mixin/demoModeValuesMixin.js';
import downloadCsvFile from '@/components/downloadCsvFile';

let self;

export default {
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('frontend_groovemail_admin_block_emails') };
  },

  mixins: [demoModeValuesMixin, monthsMixin],

  data() {
    return {
      blockListEmailListing: adminUrls.blocklistsUrls.blockListEmails,
      confirmId: null,
      model: {
        emails: '',
      },
      blacklistedEmails: [],
      blacklistMailTableColumns: ['email', 'created_at', 'actions'],
      blacklistMailTableOptions: {
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        pagination: { chunk: 5 },
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        headings: {
          email: () => this.$t('frontend_groovemail_admin_block_emails'),
          created_at: () => this.$t('frontend_groovemail_created_at'),
          actions: () => this.getDataActions(),
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        filterable: ['email'],
        sortable: ['email', 'created_at'],
        requestFunction(data) {
          const requestParams = { ...data, ascending: 1 - data.ascending };
          const self = this;
          return axios
            .get(self.url, {
              params: requestParams,
            })
            .catch((e) => {
              self.dispatch('error', e);
              self.hideLoader();
            });
        },
        responseAdapter({ data }) {
          if(self.demoMode) {
            const newData = data[0]?.data.map((obj) => ({
              ...obj,
              email: self.basicEmailText,
            }));

            return {
              data: newData,
              count: newData.length,
            };
          }

          return {
            data: data[0].data,
            count: data[0].total,
          };
        },
      },

      confirmFileTarget: null,
    };
  },

  components: {
    ServerTable,
  },

  created() {
    self = this;
  },

  watch: {
    // Used to refresh black list table when demo mode changes
    demoMode() {
      this.$refs.serverTable.$refs.blacklistTable.refresh();
    },
  },

  methods: {
    ...mapActions('loader', ['LOADING']),

    // show loader
    showLoader() {
      this.LOADING(true);
    },

    // hide loader
    hideLoader (){
      this.LOADING(false);
    },

    /**
     * Returns column heading for total records
     * @returns {string} column heading for total records
     */
    getDataActions() {
      return this.$refs &&
        this.$refs.serverTable &&
        this.$refs.serverTable.$refs.blacklistTable &&
        this.$refs.serverTable.$refs.blacklistTable.count
        ? `${this.$t(
            'frontend_groovemail_admin_blocklists_total_records_label'
          )}: ${this.$refs.serverTable.$refs.blacklistTable.count}`
        : `${this.$t(
            'frontend_groovemail_admin_blocklists_total_records_label'
          )}: 0`;
    },

    // Used to download sample blacklisting emails
    async downloadSampleForBlacklistingEmails() {
      await downloadCsvFile(
        adminUrls.blocklistsUrls.downloadSampleForBlockedEmails,
        null,
        'emails.csv'
      );
    },

    // Opens blacklist modal
    openEmailBlacklistModal() {
      this.$root.$emit('modal-open', 'blacklist-email-modal');
      this.model.emails = '';
    },

    // Closes blacklist modal
    closeEmailBlacklistModal() {
      this.$root.$emit('modal-close', 'blacklist-email-modal');
    },


    // open delete blacklisted modal
    async openDeleteBlacklistedModal(id) {
      // set the Confirm id
      this.confirmId = id;

      // open the modal
      this.$refs[`confirmation-delete-blacklisted`].open();
    },

    // close delete blacklisted modal
    closeDeleteBlacklistedModal() {
      // reset the Confirm id
      this.confirmId = null;
    },

    // Used to remove blacklisted emails
    async removeBlacklistedMail() {
      try {
        await deleteData(`${this.blockListEmailListing}/${this.confirmId}`);
        const emailData = await getData(
          `${this.blockListEmailListing}?query=${this.$refs.serverTable.$refs.blacklistTable.filteredQuery}&limit=${this.blacklistMailTableOptions.perPage}&page=${this.$refs.serverTable.$refs.blacklistTable.Page}&ascending=1&byColumn=0`
        );
        this.$refs.serverTable.updateData(
          emailData?.data[0]?.data,
          emailData?.data[0]?.total
        );
      } catch (error) {
        // show error alert
        showRequestErrorAlert(error?.response?.data?.error);
      }
    },

    // It is used to upload suppression data.
    async onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files;

      // set the file target
      this.confirmFileTarget = files;

      if (!files.length) return;

      // open the modal
      this.$refs[`confirmation-import-csv`].open();
    },
    
    // confirm file change
    async confirmFileChnage() {
      const formData = new FormData();
      formData.append('csv', this.confirmFileTarget[0]);
      
      try {
        await postData(adminUrls.blocklistsUrls.blockListEmailsAddViaImport, formData);
      } catch (error) {
        // show error alert
        showRequestErrorAlert(error?.response?.data?.error);
      }
    
      this.$refs.serverTable.$refs.blacklistTable.refresh();

      document.getElementById('upload').value = '';
    },

    // close confirm file change
    closeFileChnageConfirm() {
      this.confirmFileTarget = null;
    },

    // Used to add emails to blacklist
    async addEmailsToBlacklist() {
      if (this.model.emails.trim()) {
        const emailData = this.model.emails.trim();
        const splitted = emailData.split('\n');
        splitted.forEach((element) => {
          if (element.trim()) {
            this.blacklistedEmails.push(element.trim().toLowerCase());
          }
        });
      }
      this.closeEmailBlacklistModal();
      await postData(this.blockListEmailListing, {
        blacklistEmails: this.blacklistedEmails,
      });
      this.$refs.serverTable.$refs.blacklistTable.refresh();
      this.blacklistedEmails = [];
      this.model.emails = '';
    },
  },

  computed: {
    // Used to return demoMode value from store.
    demoMode() {
      return this.$store.state.demoMode.isDemoMode;
    },

    // Returns styling for Import Emails button
    blockEmailButtonViaImport() {
      return {
        '--import-email-text': this.$t(
          'frontend_groovemail_admin_block_emails_via_import'
        ),
      };
    },

    /**
     * Returns details of columns for block domain table data
     * @returns {array} slot columns data for block domain table 
     */
    blockEmailTableSlotData() {
      return [
        {
          slotName: 'created_at',
          slotType: 'computed',
          classes: () => {
            return '';
          },
          value: (props) => {
            return props.row.created_at
              ? `${new Date(props.row.created_at).getDate()} ${
                  this.months[new Date(props.row.created_at).getMonth()]
                }, ${new Date(props.row.created_at).getFullYear()}`
              : this.$t('frontend_groovemail_not_available');
          },
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          conditions: () => {
            return true;
          },
          classes: () => {
            return {
              outerDiv: '',
              dropdown: '',
              outerSpan: '',
            };
          },
          actionList: [
            {
              id: 0,
              name: () => this.$t('frontend_delete'),
              conditions: () => {
                return true;
              },
              callback: (props) => this.openDeleteBlacklistedModal(props.row.id),
            },
          ],
        },
      ];
    },
  },
};
</script>


<style lang="scss">
.groovemail-blocklist-email-table #gd-server-table-view {
  .VueTables__table th:last-child {
    text-align: right;
  }

  .VueTables__table th:last-child span {
    display: inline-block;
  }
}
</style>
